import { useState, createContext, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    LinearProgress,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isSameMonth } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { WorkLog, TimeOff } from "./types";
import { WorkLogModal } from "./components/WorkLogModal";
import { DaySummary } from "./components/DaySummary";
import { DateFilter } from "./components/DateFilter";
import { EmployeeSummary } from "./components/EmployeeSummary";
import { useRecords } from "../../api/useRecords";
import { startOfMonth, endOfMonth } from "date-fns";
import { ArrowBack, ExpandMore, Flag } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { StaffWorkLogsContextType } from "./types";
import { SPECIAL_USERS } from "../../utils/constants";
import {
    calculateHoursTotals,
    calculateTimeOffsSummary,
    countWorkDaysOfAtLeastFiveHours,
    calculateWorkedHoursByWeek,
    calculateTimeOffByWeek,
    computeCumulativeHours,
    getEffectiveCumulativeToDate,
    resolveCumulativeFromDate,
    hasValidDayOffConfig,
    buildClinicHolidaySet,
    resolveClinicId,
    sumExtraHoursPaidWithinWindow,
} from "./utils/calculations";
import {
    getCurrentRunStartDate,
    getCurrentRunBounds,
    filterTimeOffForBag,
    hasUnknownAgreementType,
} from "./utils/employmentRun";
import { useStaffWorkLogsFilter } from "./utils/filters";
import { getDaysArray, formatDisplayHours } from "./utils/general";
import { useWorkLogs } from "./utils/useWorkLogs";
import { usePortugalHolidaysRange } from "../../api/usePortugalHolidays";
import { getWeekStartDate, isDateBefore } from "../../utils/dateUtils";
import { sortBy } from "lodash";
import { getTimeOffConfig, TimeOffType } from "../../utils/timeOffUtils";
import store2 from "store2";
import { getCumulativeStartDate, getExtraHoursPaidStart } from "./utils/constants";
import { resolveUserSnapshotContext } from "./utils/snapshots";
import { CollectionsContext } from "../../components/root/CollectionsProvider";
import { useStaffPayHours } from "./utils/useStaffPayHours";
import { useOwnStaffPay } from "./utils/useOwnStaffPay";
import { resolveHoursRows, resolveOwnMoney } from "./utils/payDataSources";
export const StaffWorkLogsContext = createContext<StaffWorkLogsContextType>({} as StaffWorkLogsContextType);

const groupDaysByWeek = (days: Array<Date>, startCapDate: Date) => {
    const grouped = new Map<string, Array<Date>>();
    for (const day of days) {
        const weekStart = getWeekStartDate(day);
        const cappedWeekStart = isDateBefore(weekStart, startCapDate) ? startCapDate : weekStart;
        const weekKey = format(cappedWeekStart, "yyyy-MM-dd");
        const existing = grouped.get(weekKey) || [];
        grouped.set(weekKey, [...existing, day]);
    }
    return grouped;
};

const formatWeekHeader = (days: Array<Date>, workedHours: number) => {
    const firstDay = days[0];
    const lastDay = days[days.length - 1];
    const dateRange = `${format(firstDay, "d")} a ${format(lastDay, "d")}`;
    const workedFormatted = formatDisplayHours(workedHours, 0);
    const text = `Semana de ${dateRange}: ${workedFormatted}`;
    return text;
};

export const StaffWorkLogsDetail = () => {
    const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
    const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const workLogForm = useForm();
    const { id: userId } = useParams() as { id: string };
    const { selectedYear, setSelectedYear, selectedMonth, setSelectedMonth } = useStaffWorkLogsFilter();

    const selectedMonthDate = new Date(selectedYear, selectedMonth);

    useEffect(() => {
        const today = new Date();
        const isCurrentMonth = isSameMonth(today, selectedMonthDate);
        if (isCurrentMonth) {
            const currentWeekKey = format(getWeekStartDate(today), "yyyy-MM-dd");
            setExpandedWeeks(new Set([currentWeekKey]));
        } else {
            setExpandedWeeks(new Set());
        }
    }, [selectedYear, selectedMonth]);
    const startDate = startOfMonth(selectedMonthDate);
    const endDate = endOfMonth(selectedMonthDate);

    const { workLogs, timeOff, isLoading, isError, currentUser, isCurrentUserAdmin, staffAgreements, thisYearHolidays, snapshotsByUser } =
        useWorkLogs(startDate, endDate, userId);

    const staffAgreement = staffAgreements[0];
    const weeklyWorkHoursSchedule = sortBy(staffAgreement?.staff_weekly_hours || [], (entry: any) => entry.date_start);
    const cumulativeStartDate = getCumulativeStartDate();
    const { year: extraHoursPaidStartYear, month: extraHoursPaidStartMonth } = getExtraHoursPaidStart();
    const currentYear = new Date().getFullYear();
    const holidayStartYear = cumulativeStartDate.getFullYear();

    const { data: allHolidays, isLoading: holidaysLoading } = usePortugalHolidaysRange(holidayStartYear, currentYear);

    const hasFinanceAccess = Boolean(store2.get("finance_auth"));
    const collections = useContext(CollectionsContext);
    const canReadFinancePayments =
        hasFinanceAccess && Boolean(collections?.collections.some((collection) => collection.collection === "staff_monthly_payments"));
    const isOwnPage = currentUser?.id === userId;

    const paymentsQuery = useRecords("staff_monthly_payments", "*.*", {
        extraOptions: {
            filter: {
                _and: [
                    { operations_user_id: { _eq: userId } },
                    { year: { _eq: selectedYear } },
                    { month: { _eq: selectedMonth + 1 } },
                ],
            },
        },
        enabled: canReadFinancePayments,
    });

    const allUserPaymentsQuery = useRecords(
        "staff_monthly_payments", "year,month,extra_hours_paid", {
            extraOptions: {
                filter: {
                    _and: [
                        { operations_user_id: { _eq: userId } },
                        { extra_hours_paid: { _nnull: true } },
                        { _or: [
                            { year: { _gt: extraHoursPaidStartYear } },
                            { _and: [{ year: { _eq: extraHoursPaidStartYear } }, { month: { _gte: extraHoursPaidStartMonth } }] },
                        ]},
                    ],
                },
                pageSize: -1,
            },
            enabled: canReadFinancePayments,
        }
    );

    const staffPayHoursQuery = useStaffPayHours({
        viewerId: currentUser?.id,
        userIds: [userId],
        selectedYear,
        selectedMonth,
        enabled: !hasFinanceAccess,
    });
    const ownStaffPayQuery = useOwnStaffPay({
        viewerId: currentUser?.id,
        selectedYear,
        selectedMonth,
        enabled: !hasFinanceAccess && isOwnPage,
    });

    const hoursRows = resolveHoursRows({
        hasFinanceAccess,
        canReadFinancePayments,
        hasPeopleToAsk: true,
        financeQueries: [paymentsQuery, allUserPaymentsQuery],
        financeMonthRows: paymentsQuery.data?.items ?? [],
        financeExtraHoursPaidRows: allUserPaymentsQuery.data?.items ?? [],
        staffPayHoursQuery,
    });
    const ownMoney = resolveOwnMoney({ hasFinanceAccess, isOwnPage, ownStaffPayQuery });
    const moneyAvailability = hasFinanceAccess ? hoursRows.availability : ownMoney.availability;

    const toggleRowExpansion = (date: string) => {
        setExpandedRows((prev) => ({ ...prev, [date]: !prev[date] }));
    };

    const toggleWeekExpansion = (weekKey: string) => {
        setExpandedWeeks((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(weekKey)) {
                newSet.delete(weekKey);
            } else {
                newSet.add(weekKey);
            }
            return newSet;
        });
    };

    const organizedData: { [key: string]: { workLogs: WorkLog[]; timeOffs: TimeOff[] } } = {};
    getDaysArray(startDate, endDate).forEach((date) => {
        const dateString = format(date, "yyyy-MM-dd");
        organizedData[dateString] = { workLogs: [], timeOffs: [] };
    });

    workLogs.forEach((log: WorkLog) => {
        const dateString = format(new Date(log.start_date), "yyyy-MM-dd");
        if (organizedData[dateString]) organizedData[dateString].workLogs.push(log);
    });

    timeOff.forEach((to: TimeOff) => {
        const dateString = format(new Date(to.start_date), "yyyy-MM-dd");
        if (organizedData[dateString]) organizedData[dateString].timeOffs.push(to);
    });

    if (isLoading || holidaysLoading) {
        return <LinearProgress color="secondary" />;
    }

    if (isError) {
        return <Typography>Error loading data</Typography>;
    }

    const currentRunBounds = getCurrentRunBounds(weeklyWorkHoursSchedule);
    const agreementTypeMissing = weeklyWorkHoursSchedule.length === 0 || hasUnknownAgreementType(weeklyWorkHoursSchedule);
    const totalsHours = calculateHoursTotals(workLogs, { capToMonth: selectedMonthDate, ...currentRunBounds });
    const totalsTimeOff = calculateTimeOffsSummary(startDate, endDate, timeOff, allHolidays);
    const totals = {
        totalWorkHours: totalsHours.totalWorkHours,
        effectiveWorkHours: totalsHours.effectiveWorkHours,
        totalClientHours: totalsHours.totalClientHours,
        nonClientHours: totalsHours.effectiveWorkHours - totalsHours.totalClientHours,
        totalHolidays: totalsTimeOff.daysHolidayCurrentMonthTotal,
        totalSickLeaveDays: totalsTimeOff.daysSickLeaveCurrentMonthTotal,
        totalSickLeaveHours: totalsTimeOff.hoursSickLeaveCurrentMonthTotal,
        totalMissedHours: totalsTimeOff.hoursMissedCurrentMonthTotal,
    };

    const mealAllowanceDays = countWorkDaysOfAtLeastFiveHours(workLogs, { capToMonth: selectedMonthDate, ...currentRunBounds });

    const publicHolidaysSet = buildClinicHolidaySet(allHolidays, resolveClinicId(staffAgreement?.default_clinic));

    const totalExpectedHoursCorrection = hoursRows.monthRows
        .reduce((sum: number, monthRow) => sum + (monthRow.expected_hours_correction || 0), 0);

    const currentRunStartDate = getCurrentRunStartDate(weeklyWorkHoursSchedule);
    const { windowStart, priorBalance } = resolveUserSnapshotContext(snapshotsByUser.get(userId) || [], currentRunStartDate);
    const cumulativeFromDate = resolveCumulativeFromDate(windowStart, currentRunStartDate);
    const cumulativeToDate = getEffectiveCumulativeToDate(selectedMonthDate, weeklyWorkHoursSchedule);
    const dayOffConfigMissing = !hasValidDayOffConfig(weeklyWorkHoursSchedule, cumulativeFromDate, cumulativeToDate);

    const totalExtraHoursPaid = sumExtraHoursPaidWithinWindow(hoursRows.extraHoursPaidRows, cumulativeFromDate);
    const bagTimeOffs = filterTimeOffForBag(timeOff, weeklyWorkHoursSchedule);
    const areDeductionsKnown = hoursRows.availability.state === "ready";

    const { cumulativeExpected, cumulativeWorked } = areDeductionsKnown && !dayOffConfigMissing && !agreementTypeMissing
        ? computeCumulativeHours({
            workLogs,
            timeOffs: bagTimeOffs,
            toDate: cumulativeToDate,
            weeklyWorkHoursSchedule,
            publicHolidaysSet,
            expectedHoursCorrection: totalExpectedHoursCorrection,
            extraHoursPaid: totalExtraHoursPaid,
            cumulativeStartDate: windowStart,
            employmentStartDate: currentRunStartDate,
            priorBalance,
        })
        : { cumulativeExpected: NaN, cumulativeWorked: NaN };

    const workedByWeek = calculateWorkedHoursByWeek(workLogs, startDate, endDate);
    const timeOffByWeek = calculateTimeOffByWeek(bagTimeOffs, startDate, endDate, weeklyWorkHoursSchedule);

    const daysGroupedByWeek = groupDaysByWeek(getDaysArray(startDate, endDate), startDate);

    const payment = hasFinanceAccess ? paymentsQuery.data?.items[0] || null : ownMoney.payment;
    const firstMonthRow = hoursRows.monthRows[0];
    const monthCorrections = firstMonthRow
        ? { expected_hours_correction: firstMonthRow.expected_hours_correction ?? null, extra_hours_paid: firstMonthRow.extra_hours_paid ?? null }
        : null;

    const staffWorkLogsContext: StaffWorkLogsContextType = {
        selectedYear,
        selectedMonth,
        currentUser,
        isCurrentUserAdmin,
        thisYearHolidays,
        payment,
        totals,
        userId,
        staffAgreement,
        workLogForm,
        isModalOpen,
        setIsModalOpen,
        workLogs,
        timeOff,
        holidays: allHolidays,
        mealAllowanceDays,
        cumulativeExpected,
        cumulativeWorked,
        monthlyWorkedHours: totalsHours.totalWorkHours,
        extraHoursPaidItems: hoursRows.extraHoursPaidRows,
        dayOffConfigMissing,
        agreementTypeMissing,
        priorBalance,
        publicHolidaysSet,
        cumulativeWindowStart: windowStart,
        userSnapshots: snapshotsByUser.get(userId) || [],
        payData: {
            hasFinanceAccess,
            hoursAvailability: hoursRows.availability,
            moneyAvailability,
            ownRemunerations: ownMoney.ownRemunerations,
            agreementsWithoutPayments: ownMoney.agreementsWithoutPayments,
            monthCorrections,
        },
    };

    return (
        <StaffWorkLogsContext.Provider value={staffWorkLogsContext}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    <Paper sx={{ p: 2, mb: 2, display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <DateFilter
                            selectedYear={selectedYear}
                            selectedMonth={selectedMonth}
                            setSelectedYear={setSelectedYear}
                            setSelectedMonth={setSelectedMonth}
                        />
                        {(isCurrentUserAdmin || SPECIAL_USERS.joana_director) && (
                            <IconButton component={Link} to={`/staff-work-logs?year=${selectedYear}&month=${selectedMonth + 1}`}>
                                <ArrowBack color="primary" />
                            </IconButton>
                        )}
                    </Paper>
                    <EmployeeSummary />
                    {Array.from(daysGroupedByWeek.entries()).map(([weekKey, days]) => {
                        const workedHoursForWeek = workedByWeek.get(weekKey) || 0;
                        const weekTimeOff = timeOffByWeek.get(weekKey);
                        const isWeekExpanded = expandedWeeks.has(weekKey);
                        const weekHasPublicHoliday = days.some((day) => publicHolidaysSet.has(format(day, "yyyy-MM-dd")));

                        const headerText = formatWeekHeader(days, workedHoursForWeek);

                        return (
                            <Accordion
                                key={weekKey}
                                expanded={isWeekExpanded}
                                onChange={() => toggleWeekExpansion(weekKey)}
                                sx={{ mb: 1, borderRadius: 1, "&:before": { display: "none" } }}
                            >
                                <AccordionSummary expandIcon={<IconButton size="small"><ExpandMore /></IconButton>}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            {headerText}
                                        </Typography>
                                        {weekHasPublicHoliday && (
                                            <Flag color="secondary" sx={{ fontSize: 20 }} />
                                        )}
                                        {weekTimeOff && Array.from(weekTimeOff.entries()).map(([type, hours]) => {
                                            const config = getTimeOffConfig(type as TimeOffType);
                                            if (!config) return null;
                                            return (
                                                <Box key={type} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: `${config.colorKey}.main`, fontSize: 13 }}>
                                                    {config.icon}
                                                    <span>{formatDisplayHours(hours, 0)}</span>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0 }}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left">Data</TableCell>
                                                    <TableCell align="center">Hora Início</TableCell>
                                                    <TableCell align="center">Primeira Sessão</TableCell>
                                                    <TableCell align="center">Hora Fim</TableCell>
                                                    <TableCell align="center">Última Sessão</TableCell>
                                                    <TableCell align="center">Horas Consideradas</TableCell>
                                                    <TableCell align="center">Horas Clientes</TableCell>
                                                    <TableCell align="center">Clínicas</TableCell>
                                                    <TableCell align="center">Detalhes</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {days.map((date) => {
                                                    const dateString = format(date, "yyyy-MM-dd");
                                                    return (
                                                        <DaySummary
                                                            key={dateString}
                                                            date={date}
                                                            expandedRows={expandedRows}
                                                            toggleRowExpansion={toggleRowExpansion}
                                                            dateString={dateString}
                                                            organizedData={organizedData}
                                                        />
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
                <WorkLogModal />
            </LocalizationProvider>
        </StaffWorkLogsContext.Provider>
    );
};
